// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// apps/admin/src/pages/billing/RefundTaskQueuePage.tsx
//
// Phase 8.2 Wave 3 Task 20 — Admin Refund Tasks Queue page (A6) with
// 4-eyes segregation indicator.
//
// vg-binding: PLAN:task-20
// vg-binding: P8.D-485
// vg-binding: P8.D-494
// vg-binding: INTERFACE-STANDARDS:error-shape
//
// Page contract:
//   - 2 status tabs per task spec: PENDING_BANK_TRANSFER + COMPLETED
//     (the canonical status enum per Phase 8.2 API contract is
//     {PENDING_BANK_TRANSFER, COMPLETED, CANCELLED} — CANCELLED is rare
//     and surfaces via a small inline filter below if present).
//   - Per-row columns: Task #, Merchant, Amount, Bank account, Created by,
//     Created at, Action.
//   - Complete button disabled when `task.self_segregation_blocked === true`
//     (server flag — same as `task.created_by === current_user.id`).
//     Tooltip: "Cannot complete refund task you created (4-eyes principle).
//     Another accountant or admin must complete."
//   - Admin override is exposed inside the RefundTaskCompleteDialog (not on
//     the row) so the override is paired with a reason + audit log entry.
//
// API:
//   GET  /api/v1/admin/refund-tasks?status=&merchant_id=&page=&limit=&sort=
//   PUT  /api/v1/admin/refund-tasks/:id/complete   (Idempotency-Key required)
//
// URL state (per VG R7 convention): ?status, ?merchant_id, ?page.
//
// Test IDs (PLAN/task-20):
//   refund-task-complete-btn-{taskId}
//   refund-task-status-tab-{value}
//   refund-task-queue-table
//   refund-task-row-{id}

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useSearchParams } from 'react-router-dom';

import { apiClient } from '@printway/shared/api';
import {
  Button,
  Card,
  CardContent,
  Input,
  cn,
  useAuth,
  useToast,
  PageHeader,
} from '@printway/ui';
import { PageListFooter } from '../../components/datatable/PageListFooter.js';
import { useTranslation } from '../../i18n/useTranslation.js';

import {
  RefundTaskCompleteDialog,
  type RefundTaskCompleteSummary,
} from '../../features/billing/RefundTaskCompleteDialog.js';

// ---------------------------------------------------------------------------
// Wire types — slim FE mirror of API-CONTRACTS GetRefundTasksResponse.
// ---------------------------------------------------------------------------

type RefundTaskStatusEnum =
  | 'PENDING_BANK_TRANSFER'
  | 'COMPLETED'
  | 'CANCELLED';

interface RefundTaskRow {
  id: string;
  refund_task_number: string;
  merchant_id: string;
  merchant_name: string;
  amount_cents: number;
  bank_account_info: string;
  status: RefundTaskStatusEnum;
  created_by: string;
  created_by_name: string;
  created_at: string;
  completed_by: string | null;
  completed_at: string | null;
  bank_reference: string | null;
  self_segregation_blocked: boolean;
  version: number;
}

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
}

interface ApiErrorEnvelope {
  ok?: false;
  error?: {
    code?: string;
    message?: string;
    user_message?: string;
  };
  success?: false;
}

type LooseClient = {
  GET: (
    path: string,
    init?: { headers?: Record<string, string> },
  ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
};

// ---------------------------------------------------------------------------
// Constants + helpers
// ---------------------------------------------------------------------------

// Phase 8.2 audit 2026-05-23 — DEFAULT_PAGE_SIZE bumped 20 → 25 to match the
// canonical /stores PageListFooter pageSizeChoices [10, 25, 50, 100]. Live
// value flows from URL-synced `pageSize` searchParam (clamped 10..100).
const DEFAULT_PAGE_SIZE = 25;

const STATUS_TABS: Array<{ value: RefundTaskStatusEnum; labelKey: string }> = [
  { value: 'PENDING_BANK_TRANSFER', labelKey: 'refund_task.queue.tab.pendingBankTransfer' },
  { value: 'COMPLETED', labelKey: 'refund_task.queue.tab.completed' },
];

function formatUsdCents(cents: number): string {
  const value = (cents ?? 0) / 100;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatRelativeTime(
  iso: string | null,
  t: (key: string, vars?: Record<string, unknown>) => string,
): string {
  if (!iso) return '—';
  const created = new Date(iso).getTime();
  if (Number.isNaN(created)) return '—';
  const diffMs = Date.now() - created;
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return t('refund_task.relative.justNow');
  if (minutes < 60) return t('refund_task.relative.minutes', { count: minutes });
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return t('refund_task.relative.hours', { count: hours });
  const days = Math.floor(hours / 24);
  if (days < 30) return t('refund_task.relative.days', { count: days });
  return new Date(iso).toISOString().slice(0, 10);
}

function extractApiMessage(
  payload: unknown,
  fallback: string,
): string {
  if (payload && typeof payload === 'object') {
    const env = payload as ApiErrorEnvelope;
    if (env.error?.user_message) return env.error.user_message;
    if (env.error?.message) return env.error.message;
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function RefundTaskQueuePage(): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useAuth((s) => s.user);

  // ---- URL state ----
  const page = Math.max(
    1,
    Number.parseInt(searchParams.get('page') ?? '1', 10) || 1,
  );
  // Phase 8.2 audit 2026-05-23 — pageSize is URL-synced per /stores standard,
  // clamped to the allowlist so a hand-typed value can't break the BE cap.
  const rawPageSize = Number.parseInt(
    searchParams.get('pageSize') ?? String(DEFAULT_PAGE_SIZE),
    10,
  );
  const pageSize = Number.isFinite(rawPageSize) && rawPageSize > 0
    ? Math.min(100, Math.max(10, rawPageSize))
    : DEFAULT_PAGE_SIZE;
  const status = ((searchParams.get('status') ??
    'PENDING_BANK_TRANSFER') as RefundTaskStatusEnum);
  const merchantId = searchParams.get('merchant_id') ?? '';

  // ---- Local state ----
  const [rows, setRows] = useState<RefundTaskRow[]>([]);
  const [meta, setMeta] = useState<PaginationMeta>({
    page: 1,
    limit: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  // ---- Dialog state ----
  const [activeTask, setActiveTask] =
    useState<RefundTaskCompleteSummary | null>(null);

  // ---- URL helpers ----
  const updateParam = useCallback(
    (key: string, value: string): void => {
      const next = new URLSearchParams(searchParams);
      if (value === '' || value === undefined) next.delete(key);
      else next.set(key, value);
      if (key !== 'page') next.delete('page');
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  const setPage = useCallback(
    (n: number): void => {
      const next = new URLSearchParams(searchParams);
      if (n <= 1) next.delete('page');
      else next.set('page', String(n));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // ---- List fetch ----
  const queryString = useMemo(() => {
    const p = new URLSearchParams();
    p.set('page', String(page));
    p.set('limit', String(pageSize));
    // vg:debug F-005: BE enum (QUEUED/PROCESSING/COMPLETED/FAILED) differs
    // from FE display enum (PENDING_BANK_TRANSFER/COMPLETED). Map FE→BE for
    // the query param while preserving FE display state.
    if (status) {
      const beStatus =
        status === 'PENDING_BANK_TRANSFER' ? 'QUEUED' : status;
      p.set('status', beStatus);
    }
    if (merchantId) p.set('merchant_id', merchantId);
    // BE refund-task listQuery schema is additionalProperties:false and FIFO
    // sorts by createdAt ASC server-side per refund-task.routes.ts — no `sort`
    // param is accepted. Don't send it.
    return p.toString();
  }, [page, pageSize, status, merchantId]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async (): Promise<void> => {
      try {
        const client = apiClient() as unknown as LooseClient;
        const { data, error, response } = await client.GET(
          `/api/v1/admin/refund-tasks?${queryString}`,
        );
        if (cancelled) return;
        if (error || !response.ok) {
          toast({
            title: t('refund_task.queue.loadFailed'),
            description: extractApiMessage(error, t('refund_task.errors.generic')),
            variant: 'error',
          });
          setRows([]);
          setMeta({ page: 1, limit: DEFAULT_PAGE_SIZE, total: 0 });
          return;
        }
        // debug-bill iter 17 — BE returns wrapped shape
        //   { success: true, data: { rows: [], total, page, limit } }
        // (per refund-task.routes.ts listRefundTasksHandler). Earlier
        // FE code assumed flat `data: [...]` per API-CONTRACTS draft and
        // fell into the else branch when data was an object → setRows([])
        // → "No refund tasks" even with seeded rows. Accept both shapes
        // defensively + the third `data.data` envelope variant for the
        // unwrapped openapi-fetch case.
        const body = data as
          | {
              data?: { rows?: RefundTaskRow[]; total?: number; page?: number; limit?: number };
            }
          | {
              ok?: true;
              data?: RefundTaskRow[];
              meta?: PaginationMeta;
            }
          | { rows?: RefundTaskRow[]; total?: number; page?: number; limit?: number }
          | undefined;
        let rowsOut: RefundTaskRow[] = [];
        let metaOut: PaginationMeta = {
          page,
          limit: DEFAULT_PAGE_SIZE,
          total: 0,
        };
        if (body && typeof body === 'object') {
          const b = body as Record<string, unknown>;
          if (Array.isArray(b.data)) {
            rowsOut = b.data as RefundTaskRow[];
            metaOut = (b.meta as PaginationMeta | undefined) ?? {
              page,
              limit: DEFAULT_PAGE_SIZE,
              total: rowsOut.length,
            };
          } else if (b.data && typeof b.data === 'object') {
            const inner = b.data as Record<string, unknown>;
            if (Array.isArray(inner.rows)) {
              rowsOut = inner.rows as RefundTaskRow[];
              metaOut = {
                page: (inner.page as number | undefined) ?? page,
                limit: (inner.limit as number | undefined) ?? DEFAULT_PAGE_SIZE,
                total: (inner.total as number | undefined) ?? rowsOut.length,
              };
            }
          } else if (Array.isArray(b.rows)) {
            rowsOut = b.rows as RefundTaskRow[];
            metaOut = {
              page: (b.page as number | undefined) ?? page,
              limit: (b.limit as number | undefined) ?? DEFAULT_PAGE_SIZE,
              total: (b.total as number | undefined) ?? rowsOut.length,
            };
          }
        }
        setRows(rowsOut);
        setMeta(metaOut);
      } catch (err) {
        if (cancelled) return;
        toast({
          title: t('refund_task.toast.networkError'),
          description:
            err instanceof Error && err.message
              ? err.message
              : t('refund_task.toast.networkErrorDescription'),
          variant: 'error',
        });
        setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [queryString, refreshTick, toast, page, t]);

  // ---- Dialog open helper ----
  function openCompleteDialog(row: RefundTaskRow): void {
    setActiveTask({
      id: row.id,
      refund_task_number: row.refund_task_number,
      merchant_name: row.merchant_name,
      amount_cents: row.amount_cents,
      bank_account_info: row.bank_account_info,
      created_by: row.created_by,
      created_by_name: row.created_by_name,
      self_segregation_blocked: row.self_segregation_blocked,
      version: row.version,
    });
  }

  // ---- Render ----

  const isCompletedTab = status === 'COMPLETED';

  return (
    <div className="flex flex-col gap-6 p-6">
      {/* Page header */}
      <PageHeader title={t('refund_task.queue.title')} subtitle={t('refund_task.queue.subtitle')} />

      {/* Status tabs — 2 per task spec (PENDING_BANK_TRANSFER + COMPLETED) */}
      <div className="flex items-center gap-6 border-b border-pw-border">
        {STATUS_TABS.map((tab) => {
          const isActive = status === tab.value;
          return (
            <button
              key={tab.value}
              type="button"
              data-testid={`refund-task-status-tab-${tab.value}`}
              onClick={() => updateParam('status', tab.value)}
              className={cn(
                'relative flex items-center gap-2 pb-3 text-sm font-medium transition-colors',
                isActive
                  ? 'text-pw-primary'
                  : 'text-pw-text-muted hover:text-pw-text',
              )}
            >
              <span>{t(tab.labelKey)}</span>
              {isActive ? (
                <span className="absolute inset-x-0 -bottom-px h-0.5 bg-pw-primary" />
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Merchant filter row (collapsible — kept inline for now) */}
      {merchantId ? (
        <Card>
          <CardContent className="flex items-end gap-3 py-4">
            <div className="min-w-[260px] flex-1">
              <label className="mb-1 block text-xs font-medium text-pw-text-muted">
                {t('refund_task.filter.merchantLabel')}
              </label>
              <Input
                value={merchantId}
                onChange={(e) => updateParam('merchant_id', e.target.value)}
                aria-label={t('refund_task.filter.merchantAria')}
                placeholder={t('refund_task.filter.merchantPlaceholder')}
              />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => updateParam('merchant_id', '')}
            >
              {t('refund_task.filter.clear')}
            </Button>
          </CardContent>
        </Card>
      ) : null}

      {/* Table card */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table
              className="w-full text-sm"
              data-testid="refund-task-queue-table"
            >
              <thead className="border-b border-pw-border bg-pw-surface/60 text-left text-xs font-medium uppercase tracking-wide text-pw-text-muted">
                <tr>
                  <th className="px-4 py-3">{t('refund_task.queue.column.taskNumber')}</th>
                  <th className="px-4 py-3">{t('refund_task.queue.column.merchant')}</th>
                  <th className="px-4 py-3 text-right">{t('refund_task.queue.column.amount')}</th>
                  <th className="px-4 py-3">{t('refund_task.queue.column.bankAccount')}</th>
                  <th className="px-4 py-3">{t('refund_task.queue.column.createdBy')}</th>
                  <th className="px-4 py-3">
                    {isCompletedTab
                      ? t('refund_task.queue.column.completed')
                      : t('refund_task.queue.column.created')}
                  </th>
                  <th className="w-[140px] px-4 py-3 text-right">{t('refund_task.queue.column.action')}</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-12 text-center text-pw-text-muted"
                    >
                      {t('refund_task.queue.loading')}
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-12 text-center text-pw-text-muted"
                    >
                      {t('refund_task.queue.empty')}
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => {
                    const sameUser =
                      row.self_segregation_blocked ||
                      (currentUser?.id != null &&
                        currentUser.id === row.created_by);
                    const isPending =
                      row.status === 'PENDING_BANK_TRANSFER';
                    return (
                      <tr
                        key={row.id}
                        data-testid={`refund-task-row-${row.id}`}
                        className="border-b border-pw-border last:border-b-0 transition-colors hover:bg-pw-surface/50"
                      >
                        <td className="px-4 py-4 align-middle font-mono text-xs text-pw-text">
                          {row.refund_task_number}
                        </td>
                        <td className="px-4 py-4 align-middle font-medium text-pw-text">
                          {row.merchant_name}
                        </td>
                        <td className="px-4 py-4 align-middle text-right font-medium text-pw-text">
                          {formatUsdCents(row.amount_cents)}
                        </td>
                        <td className="px-4 py-4 align-middle text-pw-text-muted">
                          {row.bank_account_info}
                        </td>
                        <td
                          className="px-4 py-4 align-middle text-pw-text"
                          data-testid={`refund-task-created-by-${row.id}`}
                        >
                          <span
                            className={cn(
                              'inline-flex items-center gap-1',
                              sameUser && 'text-amber-700 dark:text-amber-300',
                            )}
                          >
                            {row.created_by_name || row.created_by}
                            {sameUser ? (
                              <span
                                className="rounded-full bg-amber-100 dark:bg-amber-950/40 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-amber-800 dark:text-amber-300"
                                title={t('refund_task.queue.sameUserTitle')}
                              >
                                {t('refund_task.queue.youBadge')}
                              </span>
                            ) : null}
                          </span>
                        </td>
                        <td className="px-4 py-4 align-middle text-pw-text-muted">
                          {formatRelativeTime(
                            isCompletedTab
                              ? row.completed_at
                              : row.created_at,
                            t,
                          )}
                        </td>
                        <td className="px-4 py-4 align-middle text-right">
                          {isPending ? (
                            <button
                              type="button"
                              data-testid={`refund-task-complete-btn-${row.id}`}
                              onClick={() => openCompleteDialog(row)}
                              disabled={sameUser}
                              title={
                                sameUser
                                  ? t('refund_task.queue.completeBlockedTitle')
                                  : t('refund_task.queue.completeTitle')
                              }
                              aria-disabled={sameUser ? 'true' : 'false'}
                              className={cn(
                                'inline-flex h-8 items-center justify-center rounded-md px-3 text-xs font-medium text-white transition-colors',
                                sameUser
                                  ? 'cursor-not-allowed bg-pw-text-muted/60'
                                  : 'bg-pw-primary hover:bg-pw-primary/90',
                              )}
                            >
                              {t('refund_task.queue.complete')}
                            </button>
                          ) : (
                            <span className="text-xs text-pw-text-muted">
                              {row.bank_reference
                                ? t('refund_task.queue.refPrefix', { ref: row.bank_reference })
                                : '—'}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination — canonical /stores footer (Rows-per-page + Total +
              numbered) per audit 2026-05-23 Cluster A. */}
          <div className="border-t border-pw-border px-6 py-4">
            <PageListFooter
              page={meta.page || page}
              pageSize={pageSize}
              total={meta.total}
              totalPages={Math.max(
                1,
                Math.ceil(meta.total / (meta.limit || pageSize)),
              )}
              loading={loading}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                const next = new URLSearchParams(searchParams);
                next.set('pageSize', String(size));
                next.delete('page');
                setSearchParams(next, { replace: true });
              }}
              testId="refund-task-footer"
            />
          </div>
        </CardContent>
      </Card>

      {/* Complete dialog — extracted to ../../features/billing/RefundTaskCompleteDialog */}
      <RefundTaskCompleteDialog
        open={activeTask !== null}
        onOpenChange={(o) => {
          if (!o) setActiveTask(null);
        }}
        task={activeTask}
        onSubmitted={() => {
          setActiveTask(null);
          setRefreshTick((t) => t + 1);
        }}
      />
    </div>
  );
}

export default RefundTaskQueuePage;
